// =============================================================================
// Ampellogik - rechnet allein aus dem Datum
// =============================================================================
//
// Das ist der Kern des Architekturprinzips "Inhalt und Logik sind getrennt":
// Die Stufenleitung pflegt in Directus WANN etwas faellig ist. Die Bewertung
// "kritisch" oder "im Plan" entsteht hier im Code und ist nicht
// konfigurierbar. So kann im laufenden Betrieb niemand versehentlich die
// Fristenwarnung abschalten.
//
// Die Schwellwerte stehen im README als Tabelle. Wer sie aendert, aendert sie
// hier - und nur hier.
// =============================================================================

export type AmpelStufe = 'ueberfaellig' | 'kritisch' | 'nah' | 'plan';

export interface Ampel {
  stufe: AmpelStufe;
  /** Kurzwort fuer die Plakette am Termin. */
  beschriftung: string;
  /** Tailwind-Farbklassen, damit die Zuordnung Stufe -> Farbe an einer
   *  einzigen Stelle steht und nicht in jeder Komponente neu entsteht. */
  textFarbe: string;
  randFarbe: string;
  flaecheFarbe: string;
}

const AMPELN: Record<AmpelStufe, Ampel> = {
  ueberfaellig: {
    stufe: 'ueberfaellig',
    beschriftung: 'Überfällig',
    textFarbe: 'text-ampel-kritisch',
    randFarbe: 'bg-ampel-kritisch',
    flaecheFarbe: 'bg-ampel-kritisch/10',
  },
  kritisch: {
    stufe: 'kritisch',
    beschriftung: 'Kritisch',
    textFarbe: 'text-ampel-kritisch',
    randFarbe: 'bg-ampel-kritisch',
    flaecheFarbe: 'bg-ampel-kritisch/10',
  },
  nah: {
    stufe: 'nah',
    beschriftung: 'Frist nah',
    textFarbe: 'text-ampel-nah',
    randFarbe: 'bg-ampel-nah',
    flaecheFarbe: 'bg-ampel-nah/10',
  },
  plan: {
    stufe: 'plan',
    beschriftung: 'Im Plan',
    textFarbe: 'text-ampel-plan',
    randFarbe: 'bg-ampel-plan',
    flaecheFarbe: 'bg-ampel-plan/10',
  },
};

/**
 * Tage zwischen heute und dem Termin. Negativ heisst vorbei.
 *
 * Beide Datumswerte werden auf Mitternacht Ortszeit gesetzt, bevor gerechnet
 * wird. Sonst haengt das Ergebnis von der Uhrzeit ab, und ein Termin waere um
 * 23 Uhr einen Tag naeher als um 1 Uhr.
 */
export function tageBis(datum: string, heute: Date = new Date()): number {
  const ziel = datumAusText(datum);
  if (ziel === null) return 0;

  const a = new Date(heute.getFullYear(), heute.getMonth(), heute.getDate());
  const b = new Date(ziel.getFullYear(), ziel.getMonth(), ziel.getDate());

  // Ueber die Mittagszeit rechnen, damit die Sommerzeitumstellung die
  // Differenz nicht um eine Stunde und damit um einen Tag verschiebt.
  const einTag = 24 * 60 * 60 * 1000;
  return Math.round((b.getTime() - a.getTime()) / einTag);
}

/** JJJJ-MM-TT als lokales Datum lesen. Bewusst NICHT new Date(text): das
 *  deutet die Zeichenkette als UTC und liegt abends einen Tag daneben. */
export function datumAusText(datum: string): Date | null {
  const treffer = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datum);
  if (treffer === null) return null;
  const [, jahr, monat, tag] = treffer;
  return new Date(Number(jahr), Number(monat) - 1, Number(tag));
}

/** Die Schwellwerte aus dem README. */
export function ampelFuer(tage: number): Ampel {
  if (tage < 0) return AMPELN.ueberfaellig;
  if (tage < 3) return AMPELN.kritisch;
  if (tage < 7) return AMPELN.nah;
  return AMPELN.plan;
}

/** "in 3 Tagen", "heute fällig", "seit 2 Tagen überfällig". */
export function tageInWorten(tage: number): string {
  if (tage < 0) {
    const n = Math.abs(tage);
    return n === 1 ? 'seit 1 Tag überfällig' : `seit ${String(n)} Tagen überfällig`;
  }
  if (tage === 0) return 'heute fällig';
  return tage === 1 ? 'morgen fällig' : `in ${String(tage)} Tagen`;
}

/** Datum, wie es am Termin steht: 09.09.2026 */
export function datumKurz(datum: string): string {
  const d = datumAusText(datum);
  if (d === null) return datum;
  return d.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export type DringlichkeitsGruppe = 'ueberfaellig' | 'dieseWoche' | 'spaeter';

/**
 * Grobe Gruppierung fuer die Fristen-Uebersicht: ueberfaellig, diese Woche
 * (bis 7 Tage), spaeter. Getrennt von der feineren Ampelstufe (ampelFuer),
 * die weiterhin Farbe und Text der einzelnen Zeile bestimmt - "kritisch" und
 * "nah" fallen hier beide unter "diese Woche".
 */
export function dringlichkeitsGruppe(tage: number): DringlichkeitsGruppe {
  if (tage < 0) return 'ueberfaellig';
  if (tage <= 7) return 'dieseWoche';
  return 'spaeter';
}

/** Datum, wie es in der Kopfzeile steht: Samstag, 08. August 2026 */
export function datumLang(d: Date): string {
  return d.toLocaleDateString('de-DE', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}
